import { isEmpty } from '@ember/utils';
import type Media from '@linc-technologies/linced-common/models/media';
import type Person from '@linc-technologies/linced-common/models/person';

// SnapshotLearner is the learner whose name goes in the file name.
type SnapshotLearner = Pick<Person, 'legalFirstName' | 'legalLastName'>;

// The generated PDF.
type SnapshotFile = Pick<Media, 'rawUrl' | 'fileName'>;

// TS-STANDIN(snapshots/snapshot-set): the parts getMissingSnapshotsMap reads.
interface SnapshotSetLearners {
  id: string;
  get(key: 'learners'): {
    map<U>(callback: (learner: { id: string }) => U): U[];
  };
}

// TS-STANDIN(snapshots/snapshot-set): the parts getMissingPdfsMap reads.
interface SnapshotSetSnapshots {
  id: string;
  get(key: 'snapshots'): {
    toArray(): { id: string; pdfs?: unknown }[];
  };
}

export function downloadSnapshotPdf(
  url: string,
  learner: SnapshotLearner,
  // TS-STANDIN(snapshots/snapshot): the metadata dates go in the file name.
  snapshot: { metadata: { startDate: string; endDate: string } },
): void {
  const filename = `Report_${learner.legalFirstName}_${learner.legalLastName}_${snapshot.metadata.startDate}_to_${snapshot.metadata.endDate}.pdf`;

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function openSnapshotPdf(file: SnapshotFile): void {
  const link = document.createElement('a');
  link.href = file.rawUrl;
  link.title = file.fileName;
  link.rel = 'noopener noreferrer';
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// getMissingSnapshotsMap creates a map of snapshot sets ids and learners who are current
// members of the group but do not have a snapshot generated for them
export async function getMissingSnapshotsMap<L extends { personId: string }>(
  snapshotSets: readonly SnapshotSetLearners[],
  groupLearners: L[],
): Promise<Record<string, L[]>> {
  const snapshotSetToMissingLearnersMap: Record<string, L[]> = {};

  return Promise.all(
    // eslint-disable-next-line @typescript-eslint/require-await
    snapshotSets.map(async (set) => {
      // Wait for the hasMany relationship to resolve
      // upon entering the route for the first time
      // otherwise you just get objects full of null attributes
      const snapshotSetLearners = set.get('learners');
      const snapshotSetLearnerIds = new Set(
        snapshotSetLearners.map((l) => l.id),
      );

      const setMissingLearners: L[] = [];
      snapshotSetToMissingLearnersMap[set.id] = setMissingLearners;

      if (isEmpty(snapshotSetLearners)) {
        snapshotSetToMissingLearnersMap[set.id] = groupLearners;
        return;
      }

      const missingLearners = groupLearners.filter((learner) => {
        return !snapshotSetLearnerIds.has(learner.personId);
      });

      if (missingLearners.length) {
        setMissingLearners.push(...missingLearners);
      }
    }),
  ).then(() => snapshotSetToMissingLearnersMap);
}

// getMissingPdfsMap creates a map of snapshot sets and its snapshots without pdfs
// if a pdf errored out during the snapshot creation
export async function getMissingPdfsMap(
  snapshotSets: readonly SnapshotSetSnapshots[],
): Promise<Record<string, string[]>> {
  const snapshotSetToMissingSnapshotsMap: Record<string, string[]> = {};

  return Promise.all(
    // eslint-disable-next-line @typescript-eslint/require-await
    snapshotSets.map(async (set) => {
      // TS-BUG: the snapshots hasMany has no toArray() in ember-data 5. This
      // line throws.
      const snapshotSetSnapshots = set.get('snapshots').toArray();
      if (isEmpty(snapshotSetSnapshots)) {
        // No snapshots in this set
        snapshotSetToMissingSnapshotsMap[set.id] = [];
        return;
      }

      // Track missing PDFs
      const missing: string[] = [];

      for (const snapshot of snapshotSetSnapshots) {
        const pdfs = snapshot.pdfs;
        if (isEmpty(pdfs)) {
          missing.push(snapshot.id);
        }
      }

      // Set result: either missing snapshot IDs or 0
      snapshotSetToMissingSnapshotsMap[set.id] =
        missing.length > 0 ? missing : [];
    }),
  ).then(() => snapshotSetToMissingSnapshotsMap);
}
